import React from 'react';
import clsx from 'clsx';
import {type Cell, flexRender, type RowData} from '@tanstack/react-table';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {TableUtilityCellType} from '../constants';
import {
	getTableBodyRowCellUtilityClass,
	TABLE_BODY_ROW_CELL_CLASS_NAME,
	tableBodyRowCellClasses,
	type TableBodyRowCellClasses,
} from './tableBodyRowCellClasses';
import {TableCell, type TableCellProps} from './TableCell';

type TableBodyRowCellOwnerState = WithClasses<
	TableBodyRowCellClasses,
	{
		isActionCell?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableBodyRowCellOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getTableBodyRowCellUtilityClass, classes);
};

const TableBodyRowCellRoot = styled(TableCell, {
	name: TABLE_BODY_ROW_CELL_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	minHeight: 55,
	...(ownerState.isActionCell && {
		paddingBottom: 6.5,
		paddingTop: 6.5,
	}),
}));

export interface TableBodyRowCellProps<TData extends RowData>
	extends Omit<TableCellProps<TData>, 'classes' | 'columnId' | 'width'>,
		WithClasses<TableBodyRowCellClasses> {
	cell: Cell<TData, unknown>;
}

export const TableBodyRowCellPrimitive = <TData extends RowData>({
	classes: _classes,
	className,
	cell,
	...props
}: TableBodyRowCellProps<TData>) => {
	const {id, columnDef, getSize} = cell.column;

	const ownerState: TableBodyRowCellOwnerState = {
		classes: _classes,
		className,
		isActionCell: id === TableUtilityCellType.ACTIONS,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<TableBodyRowCellRoot
			className={clsx(classes.root, className)}
			columnId={id}
			ownerState={ownerState}
			sortType={(columnDef.meta as any)?.sortType}
			horizontalAlign={(columnDef.meta as any)?.horizontalAlign}
			width={getSize()}
			{...props}
		>
			{flexRender(columnDef.cell, cell.getContext())}
		</TableBodyRowCellRoot>
	);
};

export const TableBodyRowCell = React.memo(
	TableBodyRowCellPrimitive,
) as typeof TableBodyRowCellPrimitive;

export default TableBodyRowCell;
